/**
 * Audio cache utility for PWA offline playback.
 * Communicates with the Service Worker to cache/check/remove audio files.
 */

import { installReloadOnServiceWorkerUpdate } from './swUpdate';

let swRegistration = null;
let messageCallbacks = {};
let callbackId = 0;

/**
 * Register the service worker and set up message handling
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Workers not supported');
    return false;
  }

  try {
    // Before register(): remembers whether this page load already had a
    // worker, so only real updates trigger the one-time reload.
    installReloadOnServiceWorkerUpdate(navigator.serviceWorker);
    swRegistration = await navigator.serviceWorker.register('/sw.js');

    navigator.serviceWorker.addEventListener('message', (event) => {
      const { type, ...data } = event.data;

      // Handle broadcast responses
      Object.values(messageCallbacks).forEach(({ resolve, expectedType }) => {
        if (type === expectedType) {
          resolve(data);
        }
      });
    });

    return true;
  } catch (error) {
    console.error('SW registration failed:', error);
    return false;
  }
}

function sendMessage(message, expectedResponseType) {
  return new Promise((resolve, reject) => {
    if (!navigator.serviceWorker.controller) {
      reject(new Error('No active service worker'));
      return;
    }

    const id = ++callbackId;
    const timeout = setTimeout(() => {
      delete messageCallbacks[id];
      reject(new Error('SW message timeout'));
    }, 30000);

    messageCallbacks[id] = {
      resolve: (data) => {
        clearTimeout(timeout);
        delete messageCallbacks[id];
        resolve(data);
      },
      expectedType: expectedResponseType
    };

    navigator.serviceWorker.controller.postMessage(message);
  });
}

/**
 * Cache an audio file for offline playback
 */
export async function cacheAudioTour(audioUrl, tourId) {
  try {
    await sendMessage(
      { type: 'CACHE_AUDIO', payload: { url: audioUrl, tourId } },
      'CACHE_COMPLETE'
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Remove a cached audio file
 */
export async function removeCachedTour(audioUrl, tourId) {
  try {
    await sendMessage(
      { type: 'REMOVE_CACHED_AUDIO', payload: { url: audioUrl, tourId } },
      'REMOVE_COMPLETE'
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Check which audio URLs are cached
 * @param {string[]} urls - Array of audio URLs to check
 * @returns {Object} Map of url -> boolean
 */
export async function checkCacheStatus(urls) {
  try {
    const { results } = await sendMessage(
      { type: 'CHECK_CACHE', payload: { urls } },
      'CACHE_STATUS'
    );
    return results;
  } catch {
    return {};
  }
}

/**
 * Get total cache size info
 */
export async function getCacheSize() {
  try {
    const { size } = await sendMessage(
      { type: 'GET_CACHE_SIZE', payload: {} },
      'CACHE_SIZE'
    );
    return size;
  } catch {
    return { totalSize: 0, count: 0 };
  }
}

/**
 * Clear all cached audio
 */
export async function clearAllCache() {
  try {
    await sendMessage({ type: 'CLEAR_ALL_CACHE' }, 'CLEAR_COMPLETE');
    return true;
  } catch {
    return false;
  }
}

/**
 * Format bytes to human readable
 */
export function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Check if we're online
 */
export function isOnline() {
  return navigator.onLine;
}
